<template>
  <div>
    <div class="rounded-lg shadow bg-white p-4">
      <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
        <div>
          <div class="font-bold text-xl">{{ pageTitle }}</div>
        </div>
        <div class="inline-flex gap-2 flex-wrap justify-end">
          <button class="btn btn-outline-primary" v-on:click="openAlimtalkModal">알림톡 발송</button>
          <button class="btn btn-outline-primary" v-on:click="openAlimtalkSendRequests">발송 결과 조회</button>
          <button
            v-for="messageType in messageTypes"
            v-show="isSuperAdmin"
            :key="messageType.value"
            class="btn message-type-button"
            :class="messageType.buttonClass"
            v-on:click="openMessageModal(messageType.value)"
          >
            공지수정({{ messageType.label }})
          </button>
          <button v-if="isSuperAdmin" class="btn btn-outline-dark" v-on:click="openTypeOrderModal">
            영역 순서 변경
          </button>
          <Button :type="ButtonTypes.blue" label="결과값 세팅하기" :onClick="openSettingModal" />
          <Button :type="ButtonTypes.green" label="엑셀 다운로드" :onClick="downloadExcel" :loading="loading.excel" />
        </div>
      </div>

      <div v-if="visibleMessageTypes.length > 0" class="flex flex-col gap-4 my-4">
        <div
          v-for="messageType in visibleMessageTypes"
          :key="messageType.value"
          class="border border-transparent rounded p-1"
        >
          <div class="text-sm font-semibold mb-2">{{ messageType.label }}</div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="message in clipboardMessagesByType[messageType.value]"
              :key="message.id"
              class="btn message-type-button min-w-[108px]"
              :class="messageType.buttonClass"
              :draggable="isSuperAdmin"
              v-on:dragstart.stop="onMainMessageDragStart(messageType.value, message.id)"
              v-on:dragover.prevent
              v-on:drop.stop="onMainMessageDrop(messageType.value, message.id)"
              v-on:click="copyValue(message.contents, message.title)"
            >
              {{ message.title }}
            </button>
          </div>
        </div>
      </div>

      <div class="inline-flex gap-2 justify-start items-center mb-3 flex-wrap">
        <b-input-group class="!w-[540px]">
          <b-form-input
            placeholder="이름 검색"
            v-model="keyword"
            v-on:click="$event.target.select()"
            v-on:keydown.enter.prevent="search"
          />
          <b-input-group-append>
            <button class="btn btn-primary !w-[200px]" v-on:click="search">검색</button>
          </b-input-group-append>
        </b-input-group>
        <VueDatePicker
          v-model="selectedDate"
          range
          :teleport="true"
          format="yyyy-MM-dd"
          :enable-time-picker="false"
          :clearable="true"
          multi-calendars
          placeholder="검색 시작일, 검색 종료일"
          class="!w-[400px]"
        />
      </div>

      <div class="text-sm text-gray-500 mb-4">
        결과값 조합: {{ selectedOptionLabels.join(' / ') }}
      </div>

      <b-table :fields="fields" :items="items" class="text-left responsive">
        <template #cell(createdAt)="data">
          {{ formatCreatedAt(data.item.createdAt) }}
        </template>
        <template #cell(accessCode)="data">
          <button class="btn btn-link p-0 text-decoration-none" v-on:click="copyValue(data.item.accessCode)">
            {{ data.item.accessCode }}
          </button>
        </template>
        <template #cell(combinationResult)="data">
          <button class="btn btn-link p-0 text-decoration-none" v-on:click="copyValue(data.item.combinationResult)">
            {{ data.item.combinationResult }}
          </button>
        </template>
        <template #cell(phoneNumber)="data">
          <button class="btn btn-link p-0 text-decoration-none" v-on:click="copyValue(formatPhoneNumber(data.item.phoneNumber))">
            {{ formatPhoneNumber(data.item.phoneNumber) }}
          </button>
        </template>
        <template #cell(confirmed)="data">
          <button
            v-if="isSuperAdmin"
            class="btn btn-sm"
            :class="data.item.confirmed ? 'btn-success' : 'btn-outline-secondary'"
            v-on:click="setConfirmed(data.item)"
          >
            {{ data.item.confirmed ? '확정됨' : '확정하기' }}
          </button>
        </template>
        <template #cell(confirmedAt)="data">
          {{ formatCreatedAt(data.item.confirmedAt) }}
        </template>
        <template #cell(actions)="data">
          <button
            v-if="isSuperAdmin && showKakaoManagementHideButton"
            class="btn btn-sm btn-outline-danger whitespace-nowrap"
            v-on:click="hideFromKakaoManagement(data.item)"
          >
            목록에서 제외
          </button>
        </template>
      </b-table>
    </div>

    <AdminModal
      name="kakaoMessageTypeOrderModal"
      modal-title="카톡 메시지 영역 순서 변경"
      modal-size="md"
      :is-open="isTypeOrderModalOpen"
      :close="closeTypeOrderModal"
      :confirm="saveTypeOrder"
      confirm-button-title="순서 저장"
    >
      <div class="text-sm text-gray-500 mb-3">영역을 드래그해 노출 순서를 변경하세요.</div>
      <div
        v-for="(messageType, index) in draftMessageTypes"
        :key="messageType.value"
        class="flex items-center gap-3 border rounded px-4 py-3 mb-2 bg-white cursor-move"
        draggable="true"
        v-on:dragstart="onTypeOrderDragStart(index)"
        v-on:dragover.prevent
        v-on:drop="onTypeOrderDrop(index)"
      >
        <span class="text-gray-400">☰</span>
        <span class="font-semibold">{{ index + 1 }}. {{ messageType.label }}</span>
      </div>
    </AdminModal>

    <AdminModal
      name="kakaoResultSettingModal"
      modal-title="결과값 세팅하기"
      modal-size="lg"
      :is-open="isSettingModalOpen"
      :close="closeSettingModal"
      :confirm="applyResultFormat"
      confirm-button-title="적용"
      :confirm-disabled="draftFormatComponents.length === 0"
    >
        <div class="font-semibold mb-2">선택 가능한 항목</div>
        <div class="flex flex-wrap gap-2 mt-3">
          <button
            v-for="component in formatComponentItems"
            :key="component.value"
            class="btn btn-sm btn-outline-secondary"
            v-on:click="appendFormatComponent(component.value)"
          >
            {{ component.label }}
          </button>
        </div>

        <div class="font-semibold mt-4 mb-2">결과값 형식</div>
        <div class="border rounded p-2 min-h-[56px]">
          <div
            v-if="draftFormatComponents.length === 0"
            class="text-gray-500 p-2"
          >
            항목을 선택하세요.
          </div>
          <div
            v-for="(component, index) in draftFormatComponents"
            :key="component.id"
            class="flex items-center justify-between border rounded px-3 py-2 mb-2 bg-white cursor-move"
            draggable="true"
            v-on:dragstart="onDragStart(index)"
            v-on:dragover.prevent
            v-on:drop="onDrop(index)"
          >
            <div class="flex items-center gap-2 flex-1">
              <span class="text-gray-400 mr-2">☰</span>
              <span class="whitespace-nowrap">{{ index + 1 }}. {{ formatComponentLabelMap[component.type] }}</span>
              <b-form-input
                v-if="component.type === 'CUSTOM'"
                v-model="component.value"
                size="sm"
                placeholder="입력하지 않으면 공백"
                class="ml-2"
              />
            </div>
            <button class="btn btn-sm btn-outline-danger" v-on:click="removeFormatComponent(index)">삭제</button>
          </div>
        </div>

        <div class="bg-gray-100 rounded p-3 mt-4">
          <div class="text-gray-500 mb-1">미리보기</div>
          <div class="font-bold">{{ previewResult }}</div>
        </div>

        <div class="flex justify-end mt-4">
          <button class="btn btn-outline-secondary" v-on:click="resetDefaultOptions">기본값</button>
        </div>
    </AdminModal>

    <AdminModal
      name="kakaoClipboardMessageModal"
      :modal-title="`${activeMessageTypeLabel} 공지 관리`"
      modal-size="lg"
      :is-open="isMessageModalOpen"
      :close="closeMessageModal"
      :confirm="saveClipboardMessage"
      :confirm-button-title="isMessageFormVisible && !isMessageReorderMode ? (messageForm.id ? '수정' : '추가') : undefined"
      :confirm-disabled="!messageForm.title.trim() || !messageForm.contents.trim()"
      :loading="loading.message"
    >
      <div class="flex justify-end gap-2 mb-4">
        <button
          v-if="!isMessageReorderMode"
          class="btn btn-sm btn-primary"
          v-on:click="openNewMessageForm"
        >
          공지 추가
        </button>
        <button
          v-if="!isMessageReorderMode"
          class="btn btn-sm btn-outline-secondary"
          :disabled="activeClipboardMessages.length < 2"
          v-on:click="startMessageReorder"
        >
          순서 변경
        </button>
        <template v-else>
          <button class="btn btn-sm btn-outline-secondary" v-on:click="cancelMessageReorder">
            취소
          </button>
          <button class="btn btn-sm btn-primary" v-on:click="saveMessageOrder">
            순서 저장
          </button>
        </template>
      </div>

      <div v-if="isMessageFormVisible && !messageForm.id" class="border rounded p-3 mb-4">
        <div class="flex justify-between items-center mb-2">
          <div class="font-semibold">공지 추가</div>
          <button class="btn btn-sm btn-outline-secondary" v-on:click="closeMessageFormEditor">
            취소
          </button>
        </div>
        <b-form-group label="버튼명">
          <b-form-input
            v-model="messageForm.title"
            maxlength="100"
            placeholder="예: 첫 인사"
          />
        </b-form-group>
        <b-form-group label="공지 내용" class="mt-3">
          <b-form-textarea
            v-model="messageForm.contents"
            rows="12"
            max-rows="20"
            placeholder="카카오톡으로 복사할 내용을 입력하세요."
          />
        </b-form-group>
      </div>

      <div v-if="activeClipboardMessages.length > 0" class="mb-4">
        <div class="flex justify-between items-center mb-2">
          <div class="font-semibold">저장된 공지</div>
          <div v-if="isMessageReorderMode" class="text-sm text-gray-500">
            공지를 드래그해 순서를 정한 뒤 저장하세요.
          </div>
        </div>
        <div
          v-for="(message, index) in activeClipboardMessages"
          :key="message.id"
          class="mb-2"
        >
          <div
            class="flex justify-between items-center gap-2 border rounded px-3 py-2"
            :class="isMessageReorderMode ? 'cursor-move bg-gray-50' : 'cursor-pointer'"
            :draggable="isMessageReorderMode"
            v-on:click="toggleClipboardMessage(message)"
            v-on:dragstart="onMessageDragStart(index)"
            v-on:dragover.prevent
            v-on:drop="onMessageDrop(index)"
          >
            <span class="font-medium">
              <span v-if="isMessageReorderMode" class="text-gray-400 mr-2">☰</span>
              {{ message.title }}
            </span>
            <div class="inline-flex gap-2">
              <span v-if="!isMessageReorderMode" class="text-gray-400 self-center">
                {{ isMessageFormVisible && messageForm.id === message.id ? '▲' : '▼' }}
              </span>
              <button
                v-if="!isMessageReorderMode"
                class="btn btn-sm btn-outline-danger"
                v-on:click.stop="deleteClipboardMessage(message)"
              >
                삭제
              </button>
            </div>
          </div>

          <div v-if="isMessageFormVisible && messageForm.id === message.id" class="border rounded p-3 mt-2">
            <div class="flex justify-between items-center mb-2">
              <div class="font-semibold">공지 수정</div>
              <button class="btn btn-sm btn-outline-secondary" v-on:click="closeMessageFormEditor">
                취소
              </button>
            </div>
            <b-form-group label="버튼명">
              <b-form-input
                v-model="messageForm.title"
                maxlength="100"
                placeholder="예: 첫 인사"
              />
            </b-form-group>
            <b-form-group label="공지 내용" class="mt-3">
              <b-form-textarea
                v-model="messageForm.contents"
                rows="12"
                max-rows="20"
                placeholder="카카오톡으로 복사할 내용을 입력하세요."
              />
            </b-form-group>
          </div>
        </div>
      </div>
    </AdminModal>

    <AdminModal
      name="alimtalkBulkSendModal"
      modal-title="알림톡 대량 발송"
      modal-size="xl"
      :is-open="isAlimtalkModalOpen"
      :close="closeAlimtalkModal"
      :confirm="sendBulkAlimtalk"
      confirm-button-title="알림톡 발송하기"
      :confirm-disabled="!selectedAlimtalkTemplateApproved || alimtalkRecipients.length === 0 || alimtalkMissingParameters.length > 0 || alimtalkInvalidRecipientCount > 0"
      :loading="loading.alimtalk"
    >
      <div class="alert alert-warning">
        발송 후에는 취소할 수 없습니다. 최대 5,000명까지 업로드되며 1,000명씩 나누어 발송됩니다.
      </div>
      <div class="border rounded p-3 mb-3 bg-gray-50">
        <div class="font-semibold mb-2">CSV/엑셀 작성 예시</div>
        <div class="text-sm text-gray-500 mb-2">첫 행의 헤더는 아래 이름을 사용해주세요. CSV의 쉼표 구분과 탭 구분을 모두 지원합니다.</div>
        <pre class="whitespace-pre overflow-x-auto mb-2">name	code	phonenumber
홍길동	ks00000001	010-1234-5678
김영희	ks00000002_2	010-9876-5432</pre>
        <div class="text-danger text-sm font-semibold">주의: 전화번호는 앞자리 0이 빠지지 않도록 반드시 010-0000-0000 형식으로 입력해주세요.</div>
      </div>
      <div class="flex items-center gap-3 mb-3">
        <label class="btn btn-outline-secondary mb-0">
          대상자 업데이트
          <input type="file" accept=".csv,.xlsx,.xls" class="hidden" v-on:change="uploadAlimtalkRecipients">
        </label>
        <span v-if="loading.alimtalkRecipients">파일 확인 중...</span>
        <span v-else>
          정상: <b class="text-success">{{ alimtalkValidRecipientCount }}명</b> /
          문제: <b class="text-danger">{{ alimtalkInvalidRecipientCount }}명</b>
          <span v-if="alimtalkUploadedFileName"> · {{ alimtalkUploadedFileName }}</span>
        </span>
      </div>
      <div v-if="alimtalkUploadError" class="alert alert-danger">{{ alimtalkUploadError }}</div>
      <div class="font-semibold mb-2">발송할 알림톡 선택</div>
      <div class="flex flex-wrap gap-2 mb-3">
        <button
          v-for="template in kakaoAlimtalkTemplates"
          :key="template.id"
          class="btn"
          :class="selectedAlimtalkTemplate?.id === template.id ? 'btn-primary' : 'btn-outline-primary'"
          v-on:click="selectAlimtalkTemplate(template)"
        >
          {{ alimtalkTemplateSelectionLabel(template) }}
        </button>
      </div>
      <div v-if="kakaoAlimtalkTemplates.length === 0" class="alert alert-info">카톡 관리 알림톡(type 5)으로 지정된 템플릿이 없습니다.</div>
      <div v-if="selectedAlimtalkTemplate && !selectedAlimtalkTemplateApproved" class="alert alert-warning">
        검수 중인 템플릿입니다. CSV와 치환 결과는 확인할 수 있지만 승인 완료 전에는 발송할 수 없습니다.
      </div>
      <div v-if="alimtalkMissingParameters.length" class="alert alert-danger">
        엑셀에 필요한 값이 없습니다: {{ alimtalkMissingParameters.join(', ') }}
      </div>
      <div v-if="alimtalkRecipients.length" class="mb-3">
        <div class="flex justify-between items-center mb-2">
          <div class="font-semibold">발송 대상자</div>
          <div class="text-sm text-gray-500">대상자를 클릭하면 개인별 메시지를 확인할 수 있습니다.</div>
        </div>
        <div class="alimtalk-recipient-list border rounded">
          <table class="table table-sm table-hover mb-0">
            <thead class="bg-gray-100 sticky top-0">
              <tr>
                <th class="w-[70px]">행</th>
                <th>이름</th>
                <th>학생코드</th>
                <th>전화번호</th>
                <th>확인 결과</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(recipient, index) in alimtalkRecipientRows"
                :key="`${recipient.row}-${recipient.phoneNumber}-${index}`"
                class="cursor-pointer"
                :class="recipient.valid ? (selectedAlimtalkRecipientIndex === index ? 'table-primary' : '') : 'table-danger'"
                v-on:click="selectedAlimtalkRecipientIndex = index"
              >
                <td>{{ recipient.row || index + 2 }}</td>
                <td>{{ recipient.templateParameters?.name || '-' }}</td>
                <td>{{ recipient.templateParameters?.code || '-' }}</td>
                <td>{{ formatPhoneNumber(recipient.phoneNumber) }}</td>
                <td :class="recipient.valid ? 'text-success' : 'text-danger'">{{ recipient.valid ? '정상' : recipient.errorMessage }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="border rounded bg-gray-50 p-4 min-h-[220px]">
        <div class="flex justify-between items-center mb-2">
          <div class="font-semibold">알림톡 내용 미리보기</div>
          <div v-if="selectedAlimtalkRecipient" class="text-sm text-gray-500">
            {{ selectedAlimtalkRecipient.templateParameters?.name || '이름 없음' }} ·
            {{ formatPhoneNumber(selectedAlimtalkRecipient.phoneNumber) }}
          </div>
        </div>
        <pre class="whitespace-pre-wrap mb-0">{{ alimtalkMessagePreview || '대상자 파일을 업로드하고 알림톡을 선택해주세요.' }}</pre>
      </div>
    </AdminModal>

    <AdminModal
      v-if="isAlimtalkResultModalOpen"
      name="alimtalkBulkSendResultModal"
      modal-title="알림톡 발송 접수 결과"
      :is-open="isAlimtalkResultModalOpen"
      :close="closeAlimtalkResultModal"
      :confirm="openCreatedAlimtalkSendRequest"
      confirm-button-title="발송 결과 조회"
    >
      <div v-if="alimtalkSendResult" class="py-3 text-lg">
        <div class="flex justify-between gap-4 py-1">
          <span>접수 성공</span>
          <b class="text-success">{{ alimtalkSendResult.acceptedCount }}개</b>
        </div>
        <div class="flex justify-between gap-4 py-1">
          <span>접수 실패</span>
          <b class="text-danger">{{ alimtalkSendResult.rejectedCount }}개</b>
        </div>
        <div class="flex justify-between gap-4 py-1">
          <span>전달 결과 대기 중</span>
          <b>{{ alimtalkSendResult.pendingCount }}개</b>
        </div>
      </div>
    </AdminModal>
  </div>
</template>

<script>
import api from "@/api/api"
import moment from "moment";
import Button from "@/components/Button.vue";
import {ButtonTypes} from "@/util/enums";
import AdminModal from "@/components/modals/AdminModal.vue";
import alimtalkTemplateApi from "@/api/alimtalkTemplate";

export default {
  components: {AdminModal, Button},
  computed: {
    ButtonTypes() {
      return ButtonTypes
    },
    pageTitle() {
      return this.isPtTeacherPage ? 'PT쌤 카톡 관리' : '카톡 관리'
    },
    selectedOptionLabels() {
      return this.formatComponents.map((component) => this.getComponentDisplayLabel(component))
    },
    previewResult() {
      return this.buildPreviewResult(this.draftFormatComponents)
    },
    clipboardMessagesByType() {
      return this.messageTypes.reduce((groups, messageType) => {
        groups[messageType.value] = this.clipboardMessages.filter(
          (message) => message.messageType === messageType.value
        )
        return groups
      }, {})
    },
    activeClipboardMessages() {
      if (this.isMessageReorderMode) {
        return this.draftClipboardMessages
      }
      return this.clipboardMessagesByType[this.activeMessageType] || []
    },
    visibleMessageTypes() {
      return this.messageTypes.filter(
        (messageType) => this.clipboardMessagesByType[messageType.value].length > 0
      )
    },
    activeMessageTypeLabel() {
      return this.messageTypes.find((messageType) => messageType.value === this.activeMessageType)?.label || ''
    },
    kakaoAlimtalkTemplates() {
      return this.alimtalkTemplates.filter((template) => template.inUse)
    },
    selectedAlimtalkTemplateApproved() {
      return this.isAlimtalkTemplateApproved(this.selectedAlimtalkTemplate)
    },
    alimtalkMissingParameters() {
      if (!this.selectedAlimtalkTemplate || this.alimtalkRecipients.length === 0) return []
      const keys = Object.keys(this.alimtalkRecipients[0].templateParameters || {}).map((key) => key.toLowerCase())
      return (this.selectedAlimtalkTemplate.templateParameters || []).filter((key) => !keys.includes(key.toLowerCase()))
    },
    alimtalkRecipientRows() {
      const requiredParameters = this.selectedAlimtalkTemplate?.templateParameters || []
      return this.alimtalkRecipients.map((recipient) => {
        const errors = []
        if (!recipient.valid && recipient.errorMessage) errors.push(recipient.errorMessage)
        const parameters = recipient.templateParameters || {}
        requiredParameters.forEach((key) => {
          if (!String(parameters[key.toLowerCase()] ?? '').trim()) errors.push(`${key} 값이 없습니다.`)
        })
        return {...recipient, valid: errors.length === 0, errorMessage: errors.join(' ')}
      })
    },
    alimtalkValidRecipientCount() {
      return this.alimtalkRecipientRows.filter((recipient) => recipient.valid).length
    },
    alimtalkInvalidRecipientCount() {
      return this.alimtalkRecipientRows.length - this.alimtalkValidRecipientCount
    },
    selectedAlimtalkRecipient() {
      return this.alimtalkRecipientRows[this.selectedAlimtalkRecipientIndex] || null
    },
    alimtalkMessagePreview() {
      if (!this.selectedAlimtalkTemplate || !this.selectedAlimtalkRecipient) return ''
      const parameters = this.selectedAlimtalkRecipient.templateParameters || {}
      return (this.selectedAlimtalkTemplate.templateContent || '').replace(/([#$])\{([A-Za-z0-9_]+)}/g, (token, _, key) => parameters[key.toLowerCase()] ?? token)
    },
  },
  data() {
    const defaultResultFormat = '${이름}${직접 입력한 값, 없으면 공백}${핸드폰 뒤 4자리}'
    const defaultFormatComponentTypes = ['NAME', 'CUSTOM', 'PHONE_LAST4']
    const isPtTeacherPage = this.$route.name === 'PtTeacherKakaoManagement'
    const showKakaoManagementHideButton = false
    return {
      isPtTeacherPage,
      showKakaoManagementHideButton,
      selectedDate: [
        moment().subtract(5, 'days').startOf('day').toDate(),
        moment().add(5, 'days').endOf('day').toDate()
      ],
      keyword: "",
      items: [],
      loading: {
        excel: false,
        message: false,
        alimtalk: false,
        alimtalkRecipients: false,
      },
      isAlimtalkModalOpen: false,
      isAlimtalkResultModalOpen: false,
      alimtalkSendResult: null,
      alimtalkTemplates: [],
      alimtalkRecipients: [],
      alimtalkUploadedFileName: '',
      alimtalkUploadError: '',
      selectedAlimtalkRecipientIndex: 0,
      selectedAlimtalkTemplate: null,
      isSuperAdmin: localStorage.getItem("IS_SUPER_ADMIN") === "true",
      isSettingModalOpen: false,
      isMessageModalOpen: false,
      isTypeOrderModalOpen: false,
      clipboardMessages: [],
      activeMessageType: isPtTeacherPage ? 'PT_TEACHER_APPLICANT' : 'APPLICANT',
      messageTypes: isPtTeacherPage ? [
        {value: 'PT_TEACHER_ENROLLED_2', label: '수강중2', buttonClass: 'message-type-enrolled-2'},
        {value: 'PT_TEACHER_ENROLLED_1', label: '수강중1', buttonClass: 'message-type-enrolled-1'},
        {value: 'PT_TEACHER_APPLICANT', label: '신청자', buttonClass: 'message-type-applicant'},
      ] : [
        {value: 'ENROLLED_2', label: '수강중2', buttonClass: 'message-type-enrolled-2'},
        {value: 'ENROLLED_1', label: '수강중1', buttonClass: 'message-type-enrolled-1'},
        {value: 'APPLICANT', label: '신청자', buttonClass: 'message-type-applicant'},
      ],
      messageForm: {
        id: null,
        title: '',
        contents: '',
        messageType: isPtTeacherPage ? 'PT_TEACHER_APPLICANT' : 'APPLICANT',
      },
      isMessageFormVisible: false,
      isMessageReorderMode: false,
      draftClipboardMessages: [],
      draggingMessageIndex: null,
      mainDraggingMessage: null,
      draftMessageTypes: [],
      draggingTypeOrderIndex: null,
      fields: [
        ...(localStorage.getItem("IS_SUPER_ADMIN") === "true" ? [{
          label: '확정 여부',
          key: 'confirmed',
        }, {
          label: '확정일자',
          key: 'confirmedAt',
        }] : []),
        {
          label: '카톡 이름',
          key: 'combinationResult',
        },
        {
          label: '연락처',
          key: 'phoneNumber',
        },
        {
          label: '학생 코드',
          key: 'accessCode',
        },
        {
          label: '신청일자',
          key: 'createdAt',
        },
        ...(localStorage.getItem("IS_SUPER_ADMIN") === "true" && showKakaoManagementHideButton ? [{
          label: '',
          key: 'actions',
        }] : []),
      ],
      storageKey: 'KAKAO_MANAGEMENT_RESULT_FORMAT_SETTING',
      defaultResultFormat,
      defaultFormatComponentTypes,
      resultFormat: defaultResultFormat,
      formatComponentSeq: 3,
      formatComponents: [
        {id: 1, type: 'NAME', value: ''},
        {id: 2, type: 'CUSTOM', value: ''},
        {id: 3, type: 'PHONE_LAST4', value: ''},
      ],
      draftFormatComponents: [
        {id: 1, type: 'NAME', value: ''},
        {id: 2, type: 'CUSTOM', value: ''},
        {id: 3, type: 'PHONE_LAST4', value: ''},
      ],
      draggingComponentIndex: null,
      formatComponentItems: [
        {value: 'NAME', label: '이름'},
        {value: 'CUSTOM', label: '년월'},
        {value: 'PHONE_LAST4', label: '핸드폰 뒤 4자리'},
      ],
      formatComponentLabelMap: {
        NAME: '이름',
        CUSTOM: '년월',
        PHONE_LAST4: '핸드폰 뒤 4자리',
      },
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
    this.restoreResultFormatSetting()
    this.loadClipboardMessages()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    openAlimtalkModal() {
      this.isAlimtalkModalOpen = true
      this.alimtalkRecipients = []
      this.alimtalkUploadedFileName = ''
      this.alimtalkUploadError = ''
      this.selectedAlimtalkRecipientIndex = 0
      this.selectedAlimtalkTemplate = null
      alimtalkTemplateApi.list({managementType: 5}, (response) => {
        this.alimtalkTemplates = response.data.data.filter((template) => template.inUse)
      }, (err) => this.showMessageError(err))
    },
    openAlimtalkSendRequests() {
      this.$router.push({name: 'AlimtalkSendRequests'})
    },
    closeAlimtalkResultModal() {
      this.isAlimtalkResultModalOpen = false
      this.alimtalkSendResult = null
    },
    openCreatedAlimtalkSendRequest() {
      const id = this.alimtalkSendResult?.sendRequestId
      this.closeAlimtalkResultModal()
      if (id) this.$router.push({name: 'AlimtalkSendRequestDetail', params: {id}})
    },
    closeAlimtalkModal() {
      this.isAlimtalkModalOpen = false
      this.alimtalkRecipients = []
      this.alimtalkUploadedFileName = ''
      this.alimtalkUploadError = ''
      this.selectedAlimtalkRecipientIndex = 0
      this.selectedAlimtalkTemplate = null
    },
    uploadAlimtalkRecipients(event) {
      const file = event.target.files?.[0]
      if (!file) return
      this.loading.alimtalkRecipients = true
      this.alimtalkUploadError = ''
      this.alimtalkRecipients = []
      alimtalkTemplateApi.previewRecipients(file, (response) => {
        this.loading.alimtalkRecipients = false
        this.alimtalkRecipients = response.data.data.recipients
        this.alimtalkUploadedFileName = file.name
        this.selectedAlimtalkRecipientIndex = 0
        this.$toast.show(`${this.alimtalkRecipients.length}명을 불러왔습니다.`, {
          position: "top-right",
          duration: 1500,
        })
      }, (err) => {
        this.loading.alimtalkRecipients = false
        this.alimtalkUploadedFileName = file.name
        this.alimtalkUploadError = err?.response?.data?.message || '파일 형식을 확인해주세요.'
        this.showMessageError(err)
      })
      event.target.value = ''
    },
    selectAlimtalkTemplate(template) {
      alimtalkTemplateApi.detail(template.id, (response) => {
        this.selectedAlimtalkTemplate = response.data.data
      }, (err) => this.showMessageError(err))
    },
    isAlimtalkTemplateApproved(template) {
      if (!template) return false
      return template.templateStatus === 'APR' ||
        template.templateStatus === 'TSC03' ||
        (template.templateStatusName || '').includes('승인')
    },
    alimtalkTemplateSelectionLabel(template) {
      const label = template.managementButtonName || template.templateName
      return this.isAlimtalkTemplateApproved(template) ? label : `${label} (검수중)`
    },
    sendBulkAlimtalk() {
      if (!this.selectedAlimtalkTemplateApproved || this.alimtalkRecipients.length === 0 || this.alimtalkInvalidRecipientCount > 0) return
      if (!window.confirm(`${this.alimtalkRecipients.length}명에게 [${this.selectedAlimtalkTemplate.templateName}] 알림톡을 발송합니다. 발송 후 취소할 수 없습니다. 계속할까요?`)) return
      this.loading.alimtalk = true
      alimtalkTemplateApi.bulkSend(this.selectedAlimtalkTemplate.id, this.alimtalkRecipients, (response) => {
        this.loading.alimtalk = false
        this.alimtalkSendResult = response.data.data
        this.closeAlimtalkModal()
        this.isAlimtalkResultModalOpen = true
      }, (err) => {
        this.loading.alimtalk = false
        this.showMessageError(err)
      })
    },
    buildParams() {
      const params = {
        keyword: this.keyword,
        format: this.resultFormat,
        startedAt: null,
        endedAt: null,
      }

      if (this.selectedDate?.length > 1) {
        if (this.selectedDate?.[0]) {
          params.startedAt = moment(this.selectedDate?.[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        }
        if (this.selectedDate?.[1]) {
          params.endedAt = moment(this.selectedDate?.[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
        }
      }

      return params
    },
    search() {
      api.requestConsulting.searchStudentCodes(this.buildParams(), (response) => {
        this.items = response.data
      }, (err) => {
        console.log(err)
      })
    },
    downloadExcel() {
      this.loading.excel = true
      api.requestConsulting.downloadStudentCodesExcel(this.buildParams(), (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.isPtTeacherPage ? 'PT쌤_카톡관리' : '카톡관리'}_${new Date().toISOString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        this.loading.excel = false
      }, (err) => {
        console.log(err)
        this.loading.excel = false
      })
    },
    setConfirmed(item) {
      this.copyValue(item.combinationResult)
      api.requestConsulting.setConfirmed({
        id: item.id,
        kakaoName: item.confirmed ? null : item.combinationResult,
      }, () => {
        this.search()
      }, (err) => {
        console.log(err)
      })
    },
    hideFromKakaoManagement(item) {
      if (!window.confirm(`'${item.combinationResult}' 신청 데이터를 카톡 관리 목록에서 제외하시겠습니까?`)) {
        return
      }
      api.requestConsulting.hideFromKakaoManagement({id: item.id}, () => {
        this.search()
        this.$toast.show('카톡 관리 목록에서 제외되었습니다.', {
          position: "top-right",
          duration: 1200
        })
      }, (err) => {
        this.showMessageError(err)
      })
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) {
        return ''
      }
      const numbers = phoneNumber.replace(/[^0-9]/g, '')
      if (numbers.length === 11) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`
      }
      if (numbers.length === 10) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`
      }
      return phoneNumber
    },
    formatCreatedAt(createdAt) {
      if (!createdAt) {
        return ''
      }
      return moment(createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    copyValue(value, label = value) {
      if (!value) {
        return
      }
      navigator.clipboard.writeText(value)
      this.$toast.show(`복사됨: ${label}`, {
        position: "top-right",
        duration: 1200
      });
    },
    handleKeydown(event) {
      if (event.key !== 'Escape') {
        return
      }
      if (this.isAlimtalkModalOpen) {
        this.closeAlimtalkModal()
      } else if (this.isTypeOrderModalOpen) {
        this.closeTypeOrderModal()
      } else if (this.isMessageModalOpen) {
        this.closeMessageModal()
      } else if (this.isSettingModalOpen) {
        this.closeSettingModal()
      }
    },
    loadClipboardMessages() {
      api.kakaoClipboardMessage.list((response) => {
        this.clipboardMessages = response.data.data
      }, (err) => {
        this.showMessageError(err)
      })
      api.kakaoClipboardMessage.listTypeOrders((response) => {
        const order = response.data.data || []
        if (order.length) {
          this.messageTypes = order.map((value) => this.messageTypes.find((type) => type.value === value)).filter(Boolean)
        }
      }, (err) => this.showMessageError(err))
    },
    openMessageModal(messageType) {
      this.activeMessageType = messageType
      this.resetMessageForm()
      this.isMessageFormVisible = false
      this.cancelMessageReorder()
      this.isMessageModalOpen = true
    },
    closeMessageModal() {
      this.isMessageModalOpen = false
      this.resetMessageForm()
      this.isMessageFormVisible = false
      this.cancelMessageReorder()
    },
    resetMessageForm() {
      this.messageForm = {
        id: null,
        title: '',
        contents: '',
        messageType: this.activeMessageType,
      }
    },
    openNewMessageForm() {
      this.resetMessageForm()
      this.isMessageFormVisible = true
    },
    closeMessageFormEditor() {
      this.resetMessageForm()
      this.isMessageFormVisible = false
    },
    toggleClipboardMessage(message) {
      if (this.isMessageReorderMode) return
      if (this.isMessageFormVisible && this.messageForm.id === message.id) {
        this.closeMessageFormEditor()
        return
      }
      this.messageForm = {
        id: message.id,
        title: message.title,
        contents: message.contents,
        messageType: message.messageType,
      }
      this.isMessageFormVisible = true
    },
    saveClipboardMessage() {
      const title = this.messageForm.title.trim()
      const contents = this.messageForm.contents.trim()
      if (!title || !contents) {
        return
      }

      this.loading.message = true
      const params = {
        id: this.messageForm.id,
        title,
        contents,
        messageType: this.messageForm.messageType,
      }
      const isUpdate = !!this.messageForm.id
      const request = isUpdate
        ? api.kakaoClipboardMessage.update
        : api.kakaoClipboardMessage.create

      request(params, () => {
        this.loading.message = false
        this.closeMessageFormEditor()
        this.loadClipboardMessages()
        this.$toast.show(isUpdate ? '공지가 수정되었습니다.' : '공지가 추가되었습니다.', {
          position: "top-right",
          duration: 1200
        })
      }, (err) => {
        this.loading.message = false
        this.showMessageError(err)
      })
    },
    deleteClipboardMessage(message) {
      if (!window.confirm(`'${message.title}' 공지를 삭제하시겠습니까?`)) {
        return
      }
      api.kakaoClipboardMessage.delete(message.id, () => {
        if (this.messageForm.id === message.id) {
          this.closeMessageFormEditor()
        }
        this.loadClipboardMessages()
        this.$toast.show('공지가 삭제되었습니다.', {
          position: "top-right",
          duration: 1200
        })
      }, (err) => {
        this.showMessageError(err)
      })
    },
    onMessageDragStart(index) {
      if (!this.isMessageReorderMode) return
      this.draggingMessageIndex = index
    },
    onMessageDrop(index) {
      if (!this.isMessageReorderMode) return
      if (this.draggingMessageIndex === null || this.draggingMessageIndex === index) return
      const reordered = [...this.draftClipboardMessages]
      const [moved] = reordered.splice(this.draggingMessageIndex, 1)
      reordered.splice(index, 0, moved)
      this.draftClipboardMessages = reordered
      this.draggingMessageIndex = null
    },
    startMessageReorder() {
      this.closeMessageFormEditor()
      this.draftClipboardMessages = [...(this.clipboardMessagesByType[this.activeMessageType] || [])]
      this.draggingMessageIndex = null
      this.isMessageReorderMode = true
    },
    cancelMessageReorder() {
      this.isMessageReorderMode = false
      this.draftClipboardMessages = []
      this.draggingMessageIndex = null
    },
    saveMessageOrder() {
      api.kakaoClipboardMessage.reorder(this.draftClipboardMessages.map((message) => message.id), () => {
        this.cancelMessageReorder()
        this.loadClipboardMessages()
        this.$toast.show('메시지 순서가 변경되었습니다.', {position: "top-right", duration: 1200})
      }, (err) => this.showMessageError(err))
    },
    onMainMessageDragStart(messageType, messageId) {
      this.mainDraggingMessage = {messageType, messageId}
    },
    onMainMessageDrop(messageType, targetMessageId) {
      const dragging = this.mainDraggingMessage
      this.mainDraggingMessage = null
      if (!dragging || dragging.messageType !== messageType || dragging.messageId === targetMessageId) return
      const reordered = [...this.clipboardMessagesByType[messageType]]
      const fromIndex = reordered.findIndex((message) => message.id === dragging.messageId)
      const toIndex = reordered.findIndex((message) => message.id === targetMessageId)
      if (fromIndex < 0 || toIndex < 0) return
      const [moved] = reordered.splice(fromIndex, 1)
      reordered.splice(toIndex, 0, moved)
      api.kakaoClipboardMessage.reorder(reordered.map((message) => message.id), () => {
        this.loadClipboardMessages()
        this.$toast.show('메시지 순서가 변경되었습니다.', {position: "top-right", duration: 1200})
      }, (err) => this.showMessageError(err))
    },
    openTypeOrderModal() {
      this.draftMessageTypes = this.messageTypes.map((messageType) => ({...messageType}))
      this.draggingTypeOrderIndex = null
      this.isTypeOrderModalOpen = true
    },
    closeTypeOrderModal() {
      this.isTypeOrderModalOpen = false
      this.draggingTypeOrderIndex = null
    },
    onTypeOrderDragStart(index) {
      this.draggingTypeOrderIndex = index
    },
    onTypeOrderDrop(index) {
      if (this.draggingTypeOrderIndex === null || this.draggingTypeOrderIndex === index) return
      const [moved] = this.draftMessageTypes.splice(this.draggingTypeOrderIndex, 1)
      this.draftMessageTypes.splice(index, 0, moved)
      this.draggingTypeOrderIndex = null
    },
    saveTypeOrder() {
      const reordered = this.draftMessageTypes.map((messageType) => ({...messageType}))
      api.kakaoClipboardMessage.reorderTypes(reordered.map((type) => type.value), () => {
        this.messageTypes = reordered
        this.closeTypeOrderModal()
        this.$toast.show('영역 순서가 변경되었습니다.', {position: "top-right", duration: 1200})
      }, (err) => this.showMessageError(err))
    },
    showMessageError(err) {
      this.$toast.error(err?.response?.data?.message || '공지 처리 중 오류가 발생했습니다.', {
        position: "top-right",
        duration: 2000
      })
    },
    openSettingModal() {
      this.draftFormatComponents = this.cloneFormatComponents(this.formatComponents)
      this.isSettingModalOpen = true
    },
    closeSettingModal() {
      this.isSettingModalOpen = false
    },
    applyResultFormat() {
      this.formatComponents = this.cloneFormatComponents(this.draftFormatComponents)
      this.resultFormat = this.buildResultFormat(this.formatComponents)
      this.saveResultFormatSetting()
      this.isSettingModalOpen = false
      // this.search()
    },
    resetDefaultOptions() {
      this.draftFormatComponents = this.createFormatComponents(this.defaultFormatComponentTypes)
    },
    appendFormatComponent(componentValue) {
      this.draftFormatComponents = [...this.draftFormatComponents, this.createFormatComponent(componentValue)]
    },
    removeFormatComponent(index) {
      this.draftFormatComponents = this.draftFormatComponents.filter((_, targetIndex) => targetIndex !== index)
    },
    onDragStart(index) {
      this.draggingComponentIndex = index
    },
    onDrop(dropIndex) {
      if (this.draggingComponentIndex === null || this.draggingComponentIndex === dropIndex) {
        return
      }

      const orderedComponents = [...this.draftFormatComponents]
      const [draggedComponent] = orderedComponents.splice(this.draggingComponentIndex, 1)
      orderedComponents.splice(dropIndex, 0, draggedComponent)
      this.draftFormatComponents = orderedComponents
      this.draggingComponentIndex = null
    },
    createFormatComponent(type, value = '') {
      this.formatComponentSeq += 1
      return {id: this.formatComponentSeq, type, value}
    },
    createFormatComponents(types) {
      return types.map((type) => this.createFormatComponent(type))
    },
    cloneFormatComponents(components) {
      return components.map((component) => this.createFormatComponent(component.type, component.value))
    },
    saveResultFormatSetting() {
      localStorage.setItem(this.storageKey, JSON.stringify({
        components: this.formatComponents.map((component) => ({
          type: component.type,
          value: component.value || '',
        })),
      }))
    },
    restoreResultFormatSetting() {
      const storedSetting = localStorage.getItem(this.storageKey)
      if (!storedSetting) {
        return
      }

      try {
        const parsedSetting = JSON.parse(storedSetting)
        const storedComponents = Array.isArray(parsedSetting?.components)
          ? parsedSetting.components
          : []
        const availableTypes = this.formatComponentItems.map((component) => component.value)
        const restoredComponents = storedComponents
          .filter((component) => availableTypes.includes(component.type))
          .map((component) => this.createFormatComponent(component.type, component.value || ''))

        if (restoredComponents.length === 0) {
          return
        }

        this.formatComponents = restoredComponents
        this.draftFormatComponents = this.cloneFormatComponents(restoredComponents)
        this.resultFormat = this.buildResultFormat(restoredComponents)
      } catch (e) {
        localStorage.removeItem(this.storageKey)
      }
    },
    getComponentDisplayLabel(component) {
      if (component.type !== 'CUSTOM') {
        return this.formatComponentLabelMap[component.type]
      }
      return component.value ? `${this.formatComponentLabelMap[component.type]}(${component.value})` : this.formatComponentLabelMap[component.type]
    },
    getFormatComponentToken(component) {
      if (component.type === 'NAME') return '${이름}'
      if (component.type === 'YEAR_MONTH') return '${년월}'
      if (component.type === 'YEAR_MONTH_DAY') return '${년월일}'
      if (component.type === 'CUSTOM') return component.value || '${직접 입력한 값, 없으면 공백}'
      return '${핸드폰 뒤 4자리}'
    },
    buildResultFormat(components) {
      const format = components.map((component) => (
        this.getFormatComponentToken(component)
      )).join('')
      return format || this.defaultResultFormat
    },
    buildPreviewResult(components) {
      return this.buildResultFormat(components)
        .replaceAll('${이름}', '홍길동')
        .replaceAll('${년월}', moment().format('YYMM'))
        .replaceAll('${년월일}', moment().format('YYMMDD'))
        .replaceAll('${직접 입력한 값, 없으면 공백}', '')
        .replaceAll('${핸드폰 뒤 4자리}', '1234')
    },
  }
}
</script>

<style scoped>
.message-type-button {
  color: #fff;
  border-color: rgba(0, 0, 0, 0.35);
}

.message-type-button:hover,
.message-type-button:focus {
  color: #fff;
  filter: brightness(0.92);
}

.message-type-enrolled-2 {
  background-color: #e99a9d;
}

.message-type-enrolled-1 {
  background-color: #b7d6ad;
}

.message-type-applicant {
  background-color: #ff9800;
}

.alimtalk-recipient-list {
  max-height: 360px;
  overflow-y: auto;
}

.alimtalk-recipient-list thead {
  z-index: 1;
}
</style>
